import * as React from "react";
import { Dimmer } from "semantic-ui-react";
import EditGrandNormCommon from "../../Editing/EditGrandNormCommon";
import Table from "../../../components/Table/Table";
import { setGrandNormExtraProperties } from "../../../utils/pdApiUtils";
import { editActions } from "../../../actions/editActions";
import _ from "lodash";
import { mainActions } from "../../../actions/mainActions";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";

/**
 * All the following props are passed from parent component
 */
export type GrandNormsTableProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  isGrandNormsLoading: boolean;
  editActions: typeof editActions;
  mainActions: typeof mainActions;
  subMenuRoute: State.LiftSubmenu | State.EscalatorSubmenu;
  groupName: Api.GroupName;
  toggleModal: () => void;
  columnFilters: Filter[];
  setColumnFilters: (
    subMenuRoute: State.LiftSubmenu | State.EscalatorSubmenu,
    columnFilter: Filter[]
  ) => void;
};

export const GrandNormsTable: React.FC<GrandNormsTableProps> = props => {
  const {
    columns,
    pdApi,
    mainState,
    editedObjectId,
    isEditActive,
    isGrandNormsLoading,
    editActions,
    mainActions,
    subMenuRoute,
    groupName,
    toggleModal,
    setColumnFilters
  } = props;
  const { grandNorms } = pdApi;
  const grandNormsAsArray = _.values(grandNorms ?? []);
  const { editSize, pageSize, isShortenTextEnabled } = mainState;

  const handleFilterChangeDebounced = React.useMemo(
    () => _.debounce((changedFilters: Filter[]) => {
      setColumnFilters(subMenuRoute, changedFilters);
    }, 500),
    [setColumnFilters, subMenuRoute]
  );

  React.useEffect(
    () => () => {
      handleFilterChangeDebounced.cancel();
    },
    [handleFilterChangeDebounced]
  );

  React.useEffect(
    () => () => {
      mainActions.setLastEditedObjectId(undefined);
    },
    [mainActions]
  );

  // Grand Norms are scoped to a single domain group per menu (e.g. Elevator
  // shows only Lift grand norms), so matching rows are shown and the
  // Domain Group column is hidden.
  const domainGroup = _.values(pdApi.domainGroups).find(
    domainGroup => domainGroup.enumName === groupName
  );
  const rows = [...grandNormsAsArray]
    .filter(grandNorm => !domainGroup || grandNorm.domainGroupId === domainGroup.enumValue)
    .sort((a: Api.GrandNorm, b: Api.GrandNorm) => a.code - b.code)
    .map(row => setGrandNormExtraProperties(row as Api.GrandNorm, pdApi));
  const selectedColumns: State.Column[] = columns["Grand Norm"].filter(
    column => column.name !== "Domain Group"
  );

  const startingPage = mainState.lastEditedObjectId
    ? (rows.findIndex(grandNorm => grandNorm.id === mainState.lastEditedObjectId) /
        mainState.pageSize) >>
      0
    : 0;
  const isGrandNormSelected = (grandNorm: Api.GrandNorm): boolean => {
    const selectedObjectId = editedObjectId ?? mainState.lastEditedObjectId;
    return !!selectedObjectId && grandNorm.id === selectedObjectId;
  };

  return (
    <div className="flex-1">
      <div className="flex-1">
        <Table
          startingPage={startingPage}
          rows={rows}
          loading={isGrandNormsLoading}
          loadingText="Fetching grand norms..."
          columns={selectedColumns}
          isHidingTextActive={isShortenTextEnabled}
          handleRowSelection={editActions.select}
          isRowSelected={isGrandNormSelected}
          pageSize={pageSize}
          handleFilterChange={handleFilterChangeDebounced}
        />
      </div>
      {isEditActive && (
        <Dimmer active={true} page={true}>
          <div
            className="edit-view"
            style={{ flex: editSize, width: editSize == 0.3 ? "500px" : "1000px" }}
          >
            <EditGrandNormCommon {...props} toggleModal={toggleModal} groupName={groupName} />
          </div>
        </Dimmer>
      )}
    </div>
  );
};

export default GrandNormsTable;
